import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
    'inline-flex items-center justify-center rounded-xl border text-base font-medium transition-all duration-200 no-underline cursor-pointer disabled:cursor-default [box-shadow:none]',
    {
        variants: {
            variant: {
                contained: [
                    'dark:text-secondary dark:bg-transparent dark:border-secondary',
                    'dark:hover:text-c1 dark:hover:bg-secondary',
                    'dark:disabled:text-c4 dark:disabled:border-c3 dark:disabled:bg-transparent',
                    'text-secondary-light bg-secondary border-transparent',
                    'hover:text-secondary-light hover:bg-c10',
                    'disabled:text-c47 disabled:border-transparent disabled:bg-c5',
                ].join(' '),
                outlined: [
                    'dark:bg-transparent dark:text-c11 dark:border-c5',
                    'dark:hover:bg-transparent dark:hover:border-secondary',
                    'dark:disabled:text-c5 dark:disabled:border-c3',
                    'bg-c22 text-secondary-light border-transparent',
                    'hover:bg-c23 hover:border-transparent',
                    'disabled:text-c4 disabled:border-transparent',
                ].join(' '),
                text: [
                    'uppercase tracking-[0.96px] font-bold text-xs border-none',
                    'dark:text-c11 text-c24',
                    '[&_svg]:text-secondary',
                    'hover:text-secondary hover:bg-transparent',
                    'dark:disabled:text-c3 disabled:text-c4',
                    'dark:disabled:[&_svg]:text-c3 disabled:[&_svg]:text-c4',
                ].join(' '),
                actions: [
                    'p-0 text-c68 dark:text-c11 border-c79 dark:border-c5',
                    '[&_svg]:text-c68 dark:[&_svg]:text-c11 [&_svg]:text-[1.3rem]',
                    'hover:text-secondary hover:border-secondary hover:bg-transparent',
                    'hover:[&_svg]:text-secondary',
                    'disabled:text-c79 dark:disabled:text-c4 disabled:border-[#CAD5E2] dark:disabled:border-c3',
                    'disabled:bg-[#E2E8F0] dark:disabled:bg-transparent',
                    'disabled:[&_svg]:text-c79 dark:disabled:[&_svg]:text-c4',
                    '[&.active]:text-c11 [&.active]:bg-secondary [&.active]:border-secondary',
                    '[&.active_svg]:text-white dark:[&.active_svg]:text-c11',
                ].join(' '),
                approved: [
                    'dark:text-success dark:border-success dark:bg-transparent',
                    'dark:[&_svg]:text-success',
                    'dark:hover:text-c1 dark:hover:bg-success',
                    'dark:hover:[&_svg]:text-c1',
                    'dark:disabled:text-c4 dark:disabled:border-c3 dark:disabled:bg-c3',
                    'text-text-secondary border-transparent bg-success',
                    '[&_svg]:text-text-secondary',
                    'hover:text-primary hover:bg-c31',
                    'hover:[&_svg]:text-primary',
                    'disabled:text-c47 disabled:border-c5 disabled:bg-c5',
                    'disabled:[&_svg]:text-c47',
                ].join(' '),
                destructive: [
                    'dark:text-error dark:bg-transparent dark:border-error',
                    'dark:hover:text-error-contrast dark:hover:bg-error',
                    'dark:disabled:text-c4 dark:disabled:border-c3 dark:disabled:bg-transparent',
                    'text-error-contrast bg-error border-transparent',
                    'hover:bg-error/90',
                    'disabled:text-c47 disabled:border-transparent disabled:bg-c5',
                ].join(' '),
                warning: [
                    'dark:text-warning dark:bg-transparent dark:border-warning',
                    'dark:hover:text-white dark:hover:bg-warning',
                    'dark:disabled:text-c4 dark:disabled:border-c3 dark:disabled:bg-transparent',
                    'text-white bg-warning border-transparent',
                    'hover:bg-warning/90',
                    'disabled:text-c47 disabled:border-transparent disabled:bg-c5',
                ].join(' '),
            },
            size: {
                small: 'h-10 min-w-10 text-sm',
                medium: 'h-12 min-w-12',
                large: 'h-14 min-w-14',
            },
        },
        compoundVariants: [
            // Padding matrix ported from MUI's own Button.js base styles (theme.ts never
            // overrode it) — contained/outlined/text/destructive/warning each need it
            // explicitly since the shared base string carries none. `actions` stays p-0.
            { variant: ['contained', 'destructive', 'warning'], size: 'medium', class: 'py-1.5 px-4' },
            { variant: ['contained', 'destructive', 'warning'], size: 'small', class: 'py-1 px-2.5' },
            { variant: ['contained', 'destructive', 'warning'], size: 'large', class: 'py-2 px-[22px]' },
            { variant: 'outlined', size: 'medium', class: 'py-[5px] px-[15px]' },
            { variant: 'outlined', size: 'small', class: 'py-[3px] px-[9px]' },
            { variant: 'outlined', size: 'large', class: 'py-[7px] px-[21px]' },
            { variant: 'text', size: 'medium', class: 'py-1.5 px-2' },
            { variant: 'text', size: 'small', class: 'py-1 px-[5px]' },
            { variant: 'text', size: 'large', class: 'py-2 px-[11px]' },
            { variant: 'approved', class: 'py-1.5 px-4' },
        ],
        defaultVariants: {
            variant: 'contained',
            size: 'medium',
        },
    },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, startIcon, endIcon, children, ...props }, ref) => {
        if (asChild) {
            return (
                <Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
                    {children}
                </Slot>
            );
        }
        return (
            <button className={cn(buttonVariants({ variant, size }), (startIcon || endIcon) && 'gap-2', className)} ref={ref} {...props}>
                {startIcon && <span className="inline-flex [&_svg]:h-[1.125em] [&_svg]:w-[1.125em]">{startIcon}</span>}
                {children}
                {endIcon && <span className="inline-flex [&_svg]:h-[1.125em] [&_svg]:w-[1.125em]">{endIcon}</span>}
            </button>
        );
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
